import { useState } from 'react';
import { Button, Field, Input, Modal, PhoneInput, Select, Switch, Textarea, useToast } from './ui.jsx';
import { api } from '../lib/api.js';

const BLANK = {
  customerId: '',
  name: '',
  address: '',
  city: '',
  state: 'FL',
  zip: '',
  contactName: '',
  contactPhone: '',
  dockHours: '06:00-16:00',
  dockDoors: 4,
  appointmentRequired: true,
  lumperRequired: true,
  checkInInstructions: '',
  parkingNotes: '',
  geofenceFeet: 800,
};

/**
 * Adding a dock to the customer it belongs to.
 *
 * Used from Warehouses ("Add a place") and from a customer's own Facilities
 * tab, where the customer is already known and does not have to be picked
 * again. Saving the address also drops a pin on the lumper map — see
 * server/services/geocode.js — so the office never types coordinates.
 */
export function AddSiteModal({ open, onClose, onCreated, customers = [], defaultCustomerId = '' }) {
  const toast = useToast();
  const [form, setForm] = useState({ ...BLANK, customerId: defaultCustomerId ? String(defaultCustomerId) : '' });
  const [busy, setBusy] = useState(false);
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="wide"
      title="Add a place we work"
      subtitle="Write the rules down here — they get sent to the crew with every job"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={async () => {
              if (!form.name) {
                toast.err('A facility name is required');
                return;
              }
              if (!form.customerId) {
                toast.err('Pick the customer this dock belongs to');
                return;
              }
              setBusy(true);
              try {
                const payload = await api.post('/sites', {
                  ...form,
                  customerId: Number(form.customerId),
                  dockDoors: Number(form.dockDoors),
                  geofenceFeet: Number(form.geofenceFeet),
                });
                onCreated(payload.data);
                onClose();
              } catch (err) {
                toast.err('Could not add the facility', err.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Add facility
          </Button>
        </>
      }
    >
      <div className="grid grid-2" style={{ gap: 14 }}>
        <Field label="Customer" required>
          <Select
            value={form.customerId}
            placeholder="Choose a customer"
            onChange={(e) => set('customerId', e.target.value)}
            options={customers.map((c) => ({ value: c.id, label: c.name }))}
          />
        </Field>
        <Field label="Name of the place" required><Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Medley DC 4" /></Field>
        <Field
          label="Address"
          className="span-2"
          hint="We look this up and drop a pin on the lumper map. Street, city and ZIP is enough."
        >
          <Input value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="10400 NW South River Drive" />
        </Field>
        <Field label="City"><Input value={form.city} onChange={(e) => set('city', e.target.value)} /></Field>
        <Field label="State / ZIP">
          <div className="row gap-8">
            <Input value={form.state} onChange={(e) => set('state', e.target.value)} maxLength={2} style={{ maxWidth: 78 }} />
            <Input value={form.zip} onChange={(e) => set('zip', e.target.value)} placeholder="33166" />
          </div>
        </Field>
        <Field label="Who to phone there"><Input value={form.contactName} onChange={(e) => set('contactName', e.target.value)} /></Field>
        <Field label="Their phone number"><PhoneInput value={form.contactPhone} onChange={(value) => set('contactPhone', value)} /></Field>
        <Field label="Open hours"><Input value={form.dockHours} onChange={(e) => set('dockHours', e.target.value)} placeholder="06:00-16:00" /></Field>
        <Field label="How many doors"><Input className="mono" type="number" value={form.dockDoors} onChange={(e) => set('dockDoors', e.target.value)} /></Field>
        <Field label="How close to allow check-in? (feet)" hint="How close the app must be to allow a check-in">
          <Input className="mono" type="number" value={form.geofenceFeet} onChange={(e) => set('geofenceFeet', e.target.value)} />
        </Field>
        <div className="col gap-10" style={{ paddingTop: 22 }}>
          <Switch label="Appointment required" checked={form.appointmentRequired} onChange={(e) => set('appointmentRequired', e.target.checked)} />
          <Switch label="Lumpers required on site" checked={form.lumperRequired} onChange={(e) => set('lumperRequired', e.target.checked)} />
        </div>
        <Field label="How to get in" className="span-2">
          <Textarea rows={3} value={form.checkInInstructions} onChange={(e) => set('checkInInstructions', e.target.value)} placeholder="Check in at the guard shack, then pull to the door number on the paperwork." />
        </Field>
        <Field label="Where to park" className="span-2">
          <Textarea rows={2} value={form.parkingNotes} onChange={(e) => set('parkingNotes', e.target.value)} placeholder="Back in only — do not pull in head first." />
        </Field>
      </div>
    </Modal>
  );
}
